import { useEffect, useRef, useState } from 'react';

/** A saved filter as the menu shows it — the API's row (T27). */
export interface SavedFilterItem {
  id: string;
  name: string;
  is_shared: boolean;
  is_default: boolean;
  is_mine: boolean;
  owner_name: string | null;
}

export interface SaveFilterInput {
  name: string;
  isShared: boolean;
  isDefault: boolean;
}

export interface SavedFiltersPanelProps {
  filters: SavedFilterItem[];
  /** The saved filter the list currently shows, if any. */
  activeId?: string | null;
  /** MANAGER and OWNER can share with the team; anyone can save a private one. */
  canShare: boolean;
  /** Something to save — the list has at least one filter on. */
  canSave: boolean;
  saving?: boolean;
  error?: string | null;
  onApply: (filter: SavedFilterItem) => void;
  onSave: (input: SaveFilterInput) => void;
  onDelete: (filter: SavedFilterItem) => void;
  onToggleDefault: (filter: SavedFilterItem) => void;
  onToggleShare: (filter: SavedFilterItem) => void;
}

function StarIcon({ filled }: { filled?: boolean }) {
  return (
    <svg
      className="h-4 w-4"
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

const ICON_BUTTON =
  'h-7 w-7 inline-flex items-center justify-center rounded-md transition-all duration-200 ' +
  'hover:bg-neutral-100 dark:hover:bg-neutral-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500';

function FilterRow({
  filter,
  active,
  canShare,
  onApply,
  onDelete,
  onToggleDefault,
  onToggleShare,
}: {
  filter: SavedFilterItem;
  active: boolean;
  canShare: boolean;
} & Pick<SavedFiltersPanelProps, 'onApply' | 'onDelete' | 'onToggleDefault' | 'onToggleShare'>) {
  return (
    <li
      className={`group flex items-center gap-1 rounded-lg px-2 py-1 transition-all duration-200 ${
        active
          ? 'bg-brand-50 dark:bg-brand-900/30'
          : 'hover:bg-neutral-50 dark:hover:bg-neutral-700/60'
      }`}
    >
      <button
        type="button"
        onClick={() => onApply(filter)}
        className="flex-1 min-w-0 text-left text-sm py-1 text-neutral-800 dark:text-neutral-100"
      >
        <span
          className={`block truncate ${active ? 'font-semibold text-brand-700 dark:text-brand-200' : ''}`}
        >
          {filter.name}
        </span>
        {!filter.is_mine && filter.owner_name && (
          <span className="block text-[11px] text-neutral-500 dark:text-neutral-400 truncate">
            by {filter.owner_name}
          </span>
        )}
      </button>
      {filter.is_shared && (
        <span className="rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/50 dark:text-violet-200 px-2 py-0.5 text-[10px] font-semibold">
          Team
        </span>
      )}
      {filter.is_mine && (
        <>
          <button
            type="button"
            aria-label={
              filter.is_default
                ? `Stop opening with ${filter.name}`
                : `Open the page with ${filter.name}`
            }
            title={filter.is_default ? 'Default — opens with this filter' : 'Use by default'}
            onClick={() => onToggleDefault(filter)}
            className={`${ICON_BUTTON} ${filter.is_default ? 'text-warning-500' : 'text-neutral-400 hover:text-warning-500'}`}
          >
            <StarIcon filled={filter.is_default} />
          </button>
          {canShare && (
            <button
              type="button"
              aria-label={
                filter.is_shared
                  ? `Stop sharing ${filter.name}`
                  : `Share ${filter.name} with the team`
              }
              title={filter.is_shared ? 'Shared with the team' : 'Share with the team'}
              onClick={() => onToggleShare(filter)}
              className={`${ICON_BUTTON} text-xs font-bold ${filter.is_shared ? 'text-violet-600' : 'text-neutral-400 hover:text-violet-600'}`}
            >
              👥
            </button>
          )}
          <button
            type="button"
            aria-label={`Delete ${filter.name}`}
            onClick={() => onDelete(filter)}
            className={`${ICON_BUTTON} text-neutral-400 hover:text-danger-600`}
          >
            ×
          </button>
        </>
      )}
    </li>
  );
}

/** The favourites list and the "Save current search" form. */
export function SavedFiltersPanel(props: SavedFiltersPanelProps) {
  const { filters, activeId, canShare, canSave, saving, error, onSave } = props;
  const [name, setName] = useState('');
  const [isShared, setShared] = useState(false);
  const [isDefault, setDefault] = useState(false);
  const mine = filters.filter((f) => f.is_mine);
  const team = filters.filter((f) => !f.is_mine);

  const rowProps = (f: SavedFilterItem) => ({
    filter: f,
    active: f.id === activeId,
    canShare,
    onApply: props.onApply,
    onDelete: props.onDelete,
    onToggleDefault: props.onToggleDefault,
    onToggleShare: props.onToggleShare,
  });

  return (
    <div className="w-80 max-w-[calc(100vw-2rem)] space-y-3">
      <div>
        <p className="px-2 text-[11px] uppercase tracking-wider font-semibold text-neutral-500 dark:text-neutral-400">
          My filters
        </p>
        {mine.length === 0 ? (
          <p className="px-2 py-1.5 text-xs text-neutral-500 dark:text-neutral-400">
            Nothing saved yet.
          </p>
        ) : (
          <ul className="mt-1 space-y-0.5">
            {mine.map((f) => (
              <FilterRow key={f.id} {...rowProps(f)} />
            ))}
          </ul>
        )}
      </div>
      {team.length > 0 && (
        <div>
          <p className="px-2 text-[11px] uppercase tracking-wider font-semibold text-neutral-500 dark:text-neutral-400">
            Shared with the team
          </p>
          <ul className="mt-1 space-y-0.5">
            {team.map((f) => (
              <FilterRow key={f.id} {...rowProps(f)} />
            ))}
          </ul>
        </div>
      )}
      <form
        className="border-t border-neutral-100 dark:border-neutral-700 pt-3 px-2 space-y-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim() || !canSave) return;
          onSave({ name: name.trim(), isShared: canShare && isShared, isDefault });
          setName('');
          setShared(false);
          setDefault(false);
        }}
      >
        <p className="text-sm font-semibold text-neutral-800 dark:text-neutral-100">
          Save current search
        </p>
        <input
          aria-label="Filter name"
          value={name}
          maxLength={80}
          placeholder="e.g. This week, unpaid"
          onChange={(e) => setName(e.target.value)}
          className="h-9 w-full rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 px-3 text-sm text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500"
        />
        <label className="flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-200">
          <input
            type="checkbox"
            checked={isDefault}
            onChange={(e) => setDefault(e.target.checked)}
            className="rounded border-neutral-300 text-brand-600 focus:ring-brand-500"
          />
          Use by default
        </label>
        <label
          className={`flex items-center gap-2 text-sm ${canShare ? 'text-neutral-700 dark:text-neutral-200' : 'text-neutral-400 dark:text-neutral-500'}`}
        >
          <input
            type="checkbox"
            checked={canShare && isShared}
            disabled={!canShare}
            onChange={(e) => setShared(e.target.checked)}
            className="rounded border-neutral-300 text-violet-600 focus:ring-violet-500 disabled:opacity-50"
          />
          Share with the team
        </label>
        {!canShare && (
          <p className="text-[11px] text-neutral-500 dark:text-neutral-400">
            Only a manager or owner can share a filter.
          </p>
        )}
        {!canSave && (
          <p className="text-[11px] text-neutral-500 dark:text-neutral-400">
            Add a filter first, then save it.
          </p>
        )}
        {error && (
          <p role="alert" className="text-xs text-danger-600 dark:text-danger-300">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={!name.trim() || !canSave || saving}
          className="h-9 w-full rounded-lg bg-gradient-to-r from-brand-600 to-indigo-600 text-sm font-semibold text-white shadow-md hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </form>
    </div>
  );
}

/** The "Favourites" button and its dropdown panel, for the FilterBar's `favorites` slot. */
export function SavedFiltersMenu(props: SavedFiltersPanelProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const active = props.filters.find((f) => f.id === props.activeId);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={`h-9 inline-flex items-center gap-1.5 rounded-lg px-3 text-sm font-semibold transition-all duration-200 ring-1 ${
          active
            ? 'bg-warning-50 text-warning-800 ring-warning-200 dark:bg-warning-900/30 dark:text-warning-100 dark:ring-warning-800'
            : 'bg-white text-neutral-700 ring-neutral-300 hover:bg-neutral-50 dark:bg-neutral-800 dark:text-neutral-200 dark:ring-neutral-600 dark:hover:bg-neutral-700'
        }`}
      >
        <span className="text-warning-500">
          <StarIcon filled={!!active} />
        </span>
        <span className="max-w-[14ch] truncate">{active ? active.name : 'Favourites'}</span>
      </button>
      {open && (
        <div className="absolute right-0 z-40 mt-1 rounded-xl bg-white dark:bg-neutral-800 shadow-lg ring-1 ring-neutral-200 dark:ring-neutral-700 p-2 animate-fade-in">
          <SavedFiltersPanel
            {...props}
            onApply={(f) => {
              props.onApply(f);
              setOpen(false);
            }}
          />
        </div>
      )}
    </div>
  );
}
